/**
 * Reflection Speaker Selector
 *
 * Simple selector for choosing who a reflection/recording is with.
 * Provides radio buttons for "primary partner" vs "someone else" with
 * a dropdown to select from saved partners or add a new one inline.
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import {
  useConversationPartners,
  usePrimaryPartner,
  useCreateConversationPartner,
} from "../../hooks/useConversationPartners";
import { useProfileOptions } from "../../hooks/useCurrentUser";
import { Loader2, Plus, User, Users } from "lucide-react";
import type { ConversationPartnerType } from "../../types/conversationPartner";
import { PARTNER_TYPE_LABELS } from "../../types/conversationPartner";
import type { SpeakerSelection } from "../../utils/speakerSelection";

// Re-export for convenience
export type { SpeakerSelection };

interface ReflectionSpeakerSelectorProps {
  /** Current selection value */
  value: SpeakerSelection;
  /** Callback when selection changes */
  onChange: (value: SpeakerSelection) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function ReflectionSpeakerSelector({
  value,
  onChange,
  disabled = false,
}: ReflectionSpeakerSelectorProps) {
  const { data: partnersData, isLoading } = useConversationPartners();
  const { data: primaryPartner } = usePrimaryPartner();
  const { data: profileOptions } = useProfileOptions();
  const createPartner = useCreateConversationPartner();

  const [showAddNew, setShowAddNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPronouns, setNewPronouns] = useState("");
  const [newType, setNewType] = useState<ConversationPartnerType>("other");

  const partners = partnersData?.partners || [];
  const nonPrimaryPartners = partners.filter((p) => !p.is_primary);

  const handleTypeChange = (newType: "primary" | "other") => {
    if (newType === "primary") {
      onChange({ type: "primary", partnerId: primaryPartner?.id || null });
      setShowAddNew(false);
    } else {
      // Default to first non-primary partner, or show add new
      const firstOther = nonPrimaryPartners[0];
      if (firstOther) {
        onChange({ type: "other", partnerId: firstOther.id });
      } else {
        setShowAddNew(true);
        onChange({ type: "new", name: "" });
      }
    }
  };

  const handlePartnerSelect = (partnerId: string) => {
    if (partnerId === "add-new") {
      setShowAddNew(true);
      onChange({ type: "new", name: "" });
    } else {
      setShowAddNew(false);
      onChange({ type: "other", partnerId });
    }
  };

  const handleAddNewPartner = async () => {
    if (!newName.trim()) return;

    try {
      const partner = await createPartner.mutateAsync({
        name: newName.trim(),
        pronouns: newPronouns || undefined,
        partner_type: newType,
        is_primary: false,
      });
      onChange({ type: "other", partnerId: partner.id });
      setShowAddNew(false);
      setNewName("");
      setNewPronouns("");
    } catch {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">
          Loading partners...
        </span>
      </div>
    );
  }

  // No partners at all - show prompt to set up
  if (!primaryPartner && partners.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4">
        <div className="flex items-start gap-3">
          <Users className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div className="flex-1">
            <p className="font-medium text-foreground">No partners set up</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Add a conversation partner to enable speaker identification.
            </p>
            {showAddNew ? (
              <div className="mt-4 space-y-3">
                <Input
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    onChange({
                      type: "new",
                      name: e.target.value,
                      pronouns: newPronouns,
                    });
                  }}
                  placeholder="Partner's name"
                  disabled={disabled}
                />
                <Select
                  value={newPronouns}
                  onValueChange={(v) => {
                    setNewPronouns(v);
                    onChange({ type: "new", name: newName, pronouns: v });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pronouns (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {profileOptions?.pronouns.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAddNewPartner}
                  disabled={
                    !newName.trim() || createPartner.isPending || disabled
                  }
                  size="sm"
                >
                  {createPartner.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Add & Continue
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setShowAddNew(true)}
                disabled={disabled}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add Partner
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium">
        Who is this conversation with?
      </Label>

      <RadioGroup
        value={value.type === "primary" ? "primary" : "other"}
        onValueChange={(v) => handleTypeChange(v as "primary" | "other")}
        disabled={disabled}
        className="space-y-3"
      >
        {/* Primary partner option */}
        {primaryPartner && (
          <div className="flex items-start space-x-3">
            <RadioGroupItem value="primary" id="speaker-primary" />
            <div className="flex-1">
              <Label
                htmlFor="speaker-primary"
                className="flex cursor-pointer items-center gap-2 font-normal"
              >
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{primaryPartner.name}</span>
                <span className="rounded bg-yellow-100 dark:bg-yellow-900 px-1.5 py-0.5 text-xs text-yellow-800 dark:text-yellow-200">
                  Primary
                </span>
              </Label>
              {primaryPartner.pronouns && (
                <p className="mt-0.5 pl-6 text-xs text-muted-foreground">
                  {primaryPartner.pronouns}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Someone else option */}
        <div className="flex items-start space-x-3">
          <RadioGroupItem value="other" id="speaker-other" />
          <div className="flex-1">
            <Label
              htmlFor="speaker-other"
              className="cursor-pointer font-normal"
            >
              Someone else
            </Label>

            {/* Partner selector (shown when "other" is selected) */}
            {value.type !== "primary" && (
              <div className="mt-3 space-y-3">
                <Select
                  value={
                    showAddNew
                      ? "add-new"
                      : value.type === "other"
                        ? value.partnerId
                        : ""
                  }
                  onValueChange={handlePartnerSelect}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a person..." />
                  </SelectTrigger>
                  <SelectContent>
                    {nonPrimaryPartners.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        <span className="flex items-center gap-2">
                          {partner.name}
                          <span className="text-xs text-muted-foreground">
                            ({PARTNER_TYPE_LABELS[partner.partner_type]})
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                    <SelectItem value="add-new">
                      <span className="flex items-center gap-2 text-primary">
                        <Plus className="h-3 w-3" />
                        Add new person
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Inline add new form */}
                {showAddNew && (
                  <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
                    <p className="text-sm font-medium">Add new person</p>
                    <Input
                      value={newName}
                      onChange={(e) => {
                        setNewName(e.target.value);
                        onChange({
                          type: "new",
                          name: e.target.value,
                          pronouns: newPronouns,
                        });
                      }}
                      placeholder="Name"
                      disabled={disabled}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={newPronouns}
                        onValueChange={(v) => {
                          setNewPronouns(v);
                          onChange({ type: "new", name: newName, pronouns: v });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pronouns" />
                        </SelectTrigger>
                        <SelectContent>
                          {profileOptions?.pronouns.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={newType}
                        onValueChange={(v) =>
                          setNewType(v as ConversationPartnerType)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PARTNER_TYPE_LABELS)
                            .filter(([key]) => key !== "primary_partner")
                            .map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleAddNewPartner}
                        disabled={
                          !newName.trim() || createPartner.isPending || disabled
                        }
                        size="sm"
                      >
                        {createPartner.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Save & Use
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowAddNew(false);
                          // Revert to first non-primary or primary
                          const firstOther = nonPrimaryPartners[0];
                          if (firstOther) {
                            onChange({
                              type: "other",
                              partnerId: firstOther.id,
                            });
                          } else if (primaryPartner) {
                            onChange({
                              type: "primary",
                              partnerId: primaryPartner.id,
                            });
                          }
                        }}
                        disabled={disabled}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}
